import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const PageContainer = styled.div`
  position: relative;
  z-index: 10;
  padding: 120px 40px 40px;
  max-width: 500px;
  margin: 0 auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const PageTitle = styled(motion.h1)`
  font-family: 'Orbitron', sans-serif;
  font-size: 36px;
  color: var(--primary);
  margin-bottom: 10px;
  text-align: center;
`;

const PageSubtitle = styled(motion.p)`
  color: var(--text-secondary);
  margin-bottom: 40px;
  font-size: 16px;
  text-align: center;
`;

const AuthCard = styled(motion.div)`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 40px;
  margin-bottom: 20px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 10px;
`;

const Tab = styled.button`
  background: ${props => props.$active ? 'rgba(0, 240, 255, 0.15)' : 'transparent'};
  color: ${props => props.$active ? 'var(--primary)' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$active ? 'var(--primary)' : 'var(--border-color)'};
  padding: 12px 24px;
  border-radius: 8px;
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 18px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 15px rgba(0, 240, 255, 0.2);
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, var(--primary), #00a0ff);
  border: none;
  border-radius: 8px;
  color: white;
  font-family: 'Orbitron', sans-serif;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 240, 255, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Message = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
  background: ${props => props.type === 'success' ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)'};
  color: ${props => props.type === 'success' ? '#00ff00' : '#ff0000'};
  border: 1px solid ${props => props.type === 'success' ? 'rgba(0, 255, 0, 0.3)' : 'rgba(255, 0, 0, 0.3)'};
`;

const API_BASE_URL = '/api/v1/auth';

const LoginPage = () => {
  const navigate = useNavigate();
  const { setAuthState } = useAuth();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  
  // 登录表单状态
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });
  
  // 注册表单状态
  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    activationCode: ''
  });
  
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleLogin = async () => {
    if (!loginForm.username || !loginForm.password) {
      setMessage({ type: 'error', text: '请输入用户名和密码' });
      return;
    }
    
    setLoading(true);
    setMessage(null);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/login`, {
        username: loginForm.username,
        password: loginForm.password
      });
      
      if (response.data.code === 1) {
        const { token, user } = response.data.data;
        
        // 使用AuthContext更新登录状态
        setAuthState(token, user);
        
        setMessage({ type: 'success', text: '登录成功！正在跳转...' });
        
        // 延迟跳转，让用户看到成功消息
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setMessage({ type: 'error', text: response.data.msg || '登录失败' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || '登录失败，请检查网络连接' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleRegister = async () => {
    if (!registerForm.username || !registerForm.email || !registerForm.password || !registerForm.confirmPassword) {
      setMessage({ type: 'error', text: '请填写所有字段' });
      return;
    }
    
    if (registerForm.password !== registerForm.confirmPassword) {
      setMessage({ type: 'error', text: '两次输入的密码不一致' });
      return;
    }
    
    if (registerForm.password.length < 6) {
      setMessage({ type: 'error', text: '密码长度至少6位' });
      return;
    }
    
    setLoading(true);
    setMessage(null);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/register`, {
        username: registerForm.username,
        email: registerForm.email,
        password: registerForm.password,
        activation_code: registerForm.activationCode
      });
      
      if (response.data.code === 1) {
        const { token, user } = response.data.data;
        
        // 使用AuthContext更新登录状态
        setAuthState(token, user);
        
        setMessage({ type: 'success', text: '注册成功！正在跳转...' });
        
        // 延迟跳转，让用户看到成功消息
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setMessage({ type: 'error', text: response.data.msg || '注册失败' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || '注册失败，请检查网络连接' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <PageContainer>
      <PageTitle
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        数字人系统
      </PageTitle>
      
      <PageSubtitle
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        登录或注册以开始使用数字人合成功能
      </PageSubtitle>
      
      <AuthCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <TabContainer>
          <Tab 
            $active={activeTab === 'login'}
            onClick={() => setActiveTab('login')}
          >
            登录
          </Tab>
          <Tab 
            $active={activeTab === 'register'}
            onClick={() => setActiveTab('register')}
          >
            注册
          </Tab>
        </TabContainer>
        
        {message && (
          <Message type={message.type}>
            {message.text}
          </Message>
        )}
        
        {activeTab === 'login' ? (
          <>
            <FormGroup>
              <Label>用户名</Label>
              <Input
                type="text"
                name="username"
                value={loginForm.username}
                onChange={handleLoginChange}
                placeholder="请输入用户名"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>密码</Label>
              <Input
                type="password"
                name="password"
                value={loginForm.password}
                onChange={handleLoginChange}
                placeholder="请输入密码"
              />
            </FormGroup>
            
            <Button onClick={handleLogin} disabled={loading}>
              {loading ? '登录中...' : '登录'}
            </Button>
          </>
        ) : (
          <>
            <FormGroup>
              <Label>用户名</Label>
              <Input
                type="text"
                name="username"
                value={registerForm.username}
                onChange={handleRegisterChange}
                placeholder="请输入用户名"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>邮箱</Label>
              <Input
                type="email"
                name="email"
                value={registerForm.email}
                onChange={handleRegisterChange}
                placeholder="请输入邮箱"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>密码</Label>
              <Input
                type="password"
                name="password"
                value={registerForm.password}
                onChange={handleRegisterChange}
                placeholder="请输入密码（至少6位）"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>确认密码</Label>
              <Input
                type="password"
                name="confirmPassword"
                value={registerForm.confirmPassword}
                onChange={handleRegisterChange}
                placeholder="请再次输入密码"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>激活码 <span style={{color: '#ff6b6b'}}>*</span></Label>
              <Input
                type="text"
                name="activationCode"
                value={registerForm.activationCode}
                onChange={handleRegisterChange}
                placeholder="请输入激活码"
              />
            </FormGroup>
            
            <Button onClick={handleRegister} disabled={loading}>
              {loading ? '注册中...' : '注册'}
            </Button>
          </>
        )}
      </AuthCard>
    </PageContainer>
  );
};

export default LoginPage;